import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { Link } from "react-router-dom";
import { useCreateGymMutation } from "../../services/gym/gym.rtk.api";
import { useGetOrganisationsQuery } from "../../services";
import { GymFormData, GymFormErrors } from "../../types/gym";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { State, Country } from "country-state-city";
import { adminRoutes } from "../../routers/AdminRouters";
import { ScreenType } from "../../types/subscription/subscriptionScreen/subscriptionScreen.type";

const GymCreate: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [createGym, { isLoading: isCreating }] = useCreateGymMutation();

  const [formData, setFormData] = useState<GymFormData>({
    name: "",
    organisation: "",
    state: "",
    city: "",
    location: "",
    contact: {
      name: "",
      email: "",
      phone: "",
    },
    screens: {
      premium: 0,
      basic: 0,
    },
  });

  const [errors, setErrors] = useState<GymFormErrors>({});

  // simple email regex for inline validation
  const emailRegex = /^\S+@\S+\.\S+$/;

  // fetch organizations
  const {
    data: organisationsData,
    isLoading: isOrgLoading,
    isError: isOrgError,
  } = useGetOrganisationsQuery();

  // normalize options (supports { data: [...] } or [...] shapes)
  const organisationOptions: Array<any> =
    (organisationsData as any)?.data ?? (organisationsData as any) ?? [];

  // Get all states from all countries
  const allStates = Country.getAllCountries().flatMap((country) =>
    State.getStatesOfCountry(country.isoCode).map((state) => ({
      name: state.name,
      countryCode: country.isoCode,
      stateCode: state.isoCode,
      countryName: country.name,
    }))
  );

  // State search
  const [stateSearch, setStateSearch] = useState("");
  const filteredStates = allStates.filter((s) =>
    s.name.toLowerCase().includes(stateSearch.toLowerCase())
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name.startsWith("contact.")) {
      const key = name.split(".")[1] as keyof GymFormData["contact"];

      // sanitize phone: keep only digits
      if (key === "phone") {
        const cleaned = value.replace(/\D/g, "");
        setFormData((prev) => ({
          ...prev,
          contact: { ...prev.contact, phone: cleaned },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          contact: { ...prev.contact, [key]: value },
        }));
      }

      // clear any existing field error when user changes value
      if (errors.contact?.[key]) {
        setErrors((prev) => ({
          ...prev,
          contact: { ...(prev.contact ?? {}), [key]: undefined },
        }));
      }
      return;
    }

    if (name.startsWith("screens.")) {
      const key = name.split(".")[1] as keyof NonNullable<
        GymFormData["screens"]
      >;
      setFormData((prev) => ({
        ...prev,
        screens: {
          ...prev.screens,
          [key]: parseInt(value) || 0,
        },
      }));
      return;
    }

    // For state selection, set both state name and code if needed
    if (name === "state") {
      setFormData((prev) => ({
        ...prev,
        state: value,
      }));
      if (errors.state) {
        setErrors((prev) => ({ ...prev, state: undefined }));
      }
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if ((errors as any)[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // inline email validation on blur
  const handleEmailBlur = () => {
    const email = formData.contact.email.trim();
    setErrors((prev) => ({
      ...prev,
      contact: {
        ...(prev.contact ?? {}),
        email:
          email && !emailRegex.test(email) ? "Email is invalid" : undefined,
      },
    }));
  };

  const incrementScreens = (type: "premium" | "basic") => {
    setFormData((prev) => ({
      ...prev,
      screens: {
        ...prev.screens,
        [type]: (prev.screens?.[type] ?? 0) + 1,
      },
    }));
  };

  const decrementScreens = (type: "premium" | "basic") => {
    setFormData((prev) => ({
      ...prev,
      screens: {
        ...prev.screens,
        [type]: Math.max(0, (prev.screens?.[type] ?? 0) - 1),
      },
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: GymFormErrors = {};

    if (!formData.name.trim()) newErrors.name = "Gym name is required";
    if (!formData.organisation.trim())
      newErrors.organisation = "Organisation is required";
    if (!formData.state.trim()) newErrors.state = "State is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";

    const contactErrors: GymFormErrors["contact"] = {};
    if (!formData.contact.name.trim())
      contactErrors.name = "Contact person is required";
    if (!formData.contact.email.trim())
      contactErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.contact.email))
      contactErrors.email = "Email is invalid";
    if (!formData.contact.phone.trim())
      contactErrors.phone = "Phone number is required";

    if (Object.keys(contactErrors).length) newErrors.contact = contactErrors;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await createGym({
        name: formData.name,
        organisation: formData.organisation,
        state: formData.state,
        city: formData.city,
        location: formData.location,
        contact: {
          name: formData.contact.name,
          email: formData.contact.email,
          phone: formData.contact.phone,
        },
        screens: {
          premium: formData.screens?.premium ?? 0,
          basic: formData.screens?.basic ?? 0,
        },
        // pass selected organization id
        organisationId: formData.organisation,
      } as any).unwrap();

      navigate(adminRoutes.gymList);
    } catch (err) {
      console.error("Create gym failed:", err);
    }
  };
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;
  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${
        theme === "dark" ? "bg-black" : "bg-gray-50"
      } p-6`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Header */}
      <div className="mb-6">
        <h1
          className={`text-2xl font-semibold ${
            theme === "dark" ? "text-gray-300" : "text-gray-800"
          }`}
        >
          Create New Gym
        </h1>
        <div className="flex items-center gap-2 mt-2 text-sm">
          <Link
            to={`${adminRoutes.gymList}`}
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            All Gyms
          </Link>
          <span
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            /
          </span>
          <span
            className={theme === "dark" ? "text-[#B6CB24]" : "text-lime-600"}
          >
            Create Gym
          </span>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Form Section */}
        <div
          className={`flex-1 ${
            theme === "dark"
              ? "bg-[#1E1E1E] border border-gray-800"
              : "bg-white border border-gray-200"
          } rounded-lg p-6`}
        >
          <div className="mb-6">
            <h2
              className={`text-base font-medium border-b pb-2 border-gray-700 ${
                theme === "dark" ? "text-white" : "text-gray-800"
              }`}
            >
              Create New Gym
            </h2>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Row 1: Gym Name and Organisation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Gym Name */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  Gym Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter here"
                  className={`w-full px-3 py-2.5 rounded-md text-sm ${
                    errors.name
                      ? "border border-red-500"
                      : theme === "dark"
                      ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                      : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              {/* Organisation */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  Organisation
                </label>
                <select
                  name="organisation"
                  value={formData.organisation}
                  onChange={handleChange}
                  disabled={isOrgLoading || isOrgError}
                  className={`w-full px-3 py-2.5 rounded-md text-sm ${
                    errors.organisation
                      ? "border border-red-500"
                      : theme === "dark"
                      ? "bg-black border border-gray-800 text-white"
                      : "bg-white border border-gray-300 text-gray-900"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500 appearance-none bg-no-repeat bg-right pr-8`}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23ADADAD' d='M5 6L0 0h10z'/%3E%3C/svg%3E")`,
                    backgroundPosition: "right 0.75rem center",
                    backgroundSize: "10px 6px",
                  }}
                >
                  <option value="">
                    {isOrgLoading
                      ? "Loading..."
                      : isOrgError
                      ? "Failed to load"
                      : "Select organisation"}
                  </option>
                  {organisationOptions.map((org: any) => {
                    const id = org.id ?? org._id;
                    const name = org.name ?? org.title ?? id;
                    return (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
                {errors.organisation && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.organisation}
                  </p>
                )}
              </div>
            </div>

            {/* Row 2: State, City, Location */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* State */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  State
                </label>

                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className={`w-full px-3 py-2.5 rounded-md text-sm ${
                    errors.state
                      ? "border border-red-500"
                      : theme === "dark"
                      ? "bg-black border border-gray-800 text-white"
                      : "bg-white border border-gray-300 text-gray-900"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500 appearance-none bg-no-repeat bg-right pr-8`}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23ADADAD' d='M5 6L0 0h10z'/%3E%3C/svg%3E")`,
                    backgroundPosition: "right 0.75rem center",
                    backgroundSize: "10px 6px",
                  }}
                >
                  <option value="">Select state</option>
                  {filteredStates.map((s) => (
                    <option
                      key={s.countryCode + "-" + s.stateCode}
                      value={s.name}
                    >
                      {s.name} ({s.countryName})
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <p className="text-red-500 text-xs mt-1">{errors.state}</p>
                )}
              </div>

              {/* City */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Enter here"
                  className={`w-full px-3 py-2.5 rounded-md text-sm ${
                    errors.city
                      ? "border border-red-500"
                      : theme === "dark"
                      ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                      : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                />
                {errors.city && (
                  <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                )}
              </div>

              {/* Location */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Enter here"
                  className={`w-full px-3 py-2.5 rounded-md text-sm ${
                    errors.location
                      ? "border border-red-500"
                      : theme === "dark"
                      ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                      : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                />
                {errors.location && (
                  <p className="text-red-500 text-xs mt-1">{errors.location}</p>
                )}
              </div>
            </div>

            {/* Row 3: Contact Person, Email, Phone Number */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Contact Person */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  Contact Person
                </label>
                <input
                  type="text"
                  name="contact.name"
                  value={formData.contact.name}
                  onChange={handleChange}
                  placeholder="Enter here"
                  className={`w-full px-3 py-2.5 rounded-md text-sm ${
                    errors.contact?.name
                      ? "border border-red-500"
                      : theme === "dark"
                      ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                      : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                />
                {errors.contact?.name && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.contact.name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  Email
                </label>
                <input
                  type="email"
                  name="contact.email"
                  value={formData.contact.email}
                  onChange={handleChange}
                  onBlur={handleEmailBlur}
                  placeholder="Enter here"
                  className={`w-full px-3 py-2.5 rounded-md text-sm ${
                    errors.contact?.email
                      ? "border border-red-500"
                      : theme === "dark"
                      ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                      : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                />
                {errors.contact?.email && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.contact.email}
                  </p>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="contact.phone"
                  value={formData.contact.phone}
                  onChange={handleChange}
                  placeholder="Enter here"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={15}
                  className={`w-full px-3 py-2.5 rounded-md text-sm ${
                    errors.contact?.phone
                      ? "border border-red-500"
                      : theme === "dark"
                      ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                      : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                  } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                />
                {errors.contact?.phone && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.contact.phone}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Premium Screen */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  {ScreenType.PREMIUM}
                </label>
                <div
                  className={`flex items-center rounded-md w-full overflow-hidden ${
                    theme === "dark"
                      ? "bg-black border border-gray-800"
                      : "bg-white border border-gray-300"
                  }`}
                >
                  {/* Decrement Button */}
                  <button
                    type="button"
                    onClick={() => decrementScreens("premium")}
                    className={`flex-shrink-0 px-2 h-10 w-10 flex items-center justify-center ${
                      theme === "dark" ? "hover:opacity-80" : "hover:opacity-70"
                    } transition-opacity`}
                    style={{ minWidth: 40 }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 28 28"
                      fill="none"
                    >
                      <path
                        d="M18.158 13.9998H9.10156"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M13.6331 25.3203C19.8853 25.3203 24.9536 20.2519 24.9536 13.9997C24.9536 7.74753 19.8853 2.67914 13.6331 2.67914C7.38089 2.67914 2.3125 7.74753 2.3125 13.9997C2.3125 20.2519 7.38089 25.3203 13.6331 25.3203Z"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {/* Input Field */}
                  <input
                    type="number"
                    name="screens.premium"
                    value={formData.screens?.premium ?? 0}
                    onChange={handleChange}
                    min="0"
                    className={`min-w-0 flex-1 px-3 py-2.5 text-sm text-center bg-transparent border-none outline-none ${
                      theme === "dark"
                        ? "text-white placeholder-gray-600"
                        : "text-gray-900 placeholder-gray-400"
                    } focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                    style={{ minWidth: 0 }}
                  />

                  {/* Increment Button */}
                  <button
                    type="button"
                    onClick={() => incrementScreens("premium")}
                    className={`flex-shrink-0 px-2 h-10 w-10 flex items-center justify-center ${
                      theme === "dark" ? "hover:opacity-80" : "hover:opacity-70"
                    } transition-opacity`}
                    style={{ minWidth: 40 }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 28 28"
                      fill="none"
                    >
                      <path
                        d="M25.6919 13.9997C25.6919 7.74753 20.6235 2.67914 14.3714 2.67914C8.11917 2.67914 3.05078 7.74753 3.05078 13.9997C3.05078 20.2518 8.11917 25.3203 14.3714 25.3203C20.6235 25.3203 25.6919 20.2518 25.6919 13.9997Z"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                      />
                      <path
                        d="M14.3681 9.4715V18.528M18.8963 13.9997H9.83984"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Normal Screen */}
              <div>
                <label
                  className={`block text-xs font-normal mb-2 ${
                    theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                  }`}
                >
                  {ScreenType.BASIC}
                </label>
                <div
                  className={`flex items-center rounded-md w-full overflow-hidden ${
                    theme === "dark"
                      ? "bg-black border border-gray-800"
                      : "bg-white border border-gray-300"
                  }`}
                >
                  {/* Decrement Button */}
                  <button
                    type="button"
                    onClick={() => decrementScreens("basic")}
                    className={`flex-shrink-0 px-2 h-10 w-10 flex items-center justify-center ${
                      theme === "dark" ? "hover:opacity-80" : "hover:opacity-70"
                    } transition-opacity`}
                    style={{ minWidth: 40 }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 28 28"
                      fill="none"
                    >
                      <path
                        d="M18.158 13.9998H9.10156"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M13.6331 25.3203C19.8853 25.3203 24.9536 20.2519 24.9536 13.9997C24.9536 7.74753 19.8853 2.67914 13.6331 2.67914C7.38089 2.67914 2.3125 7.74753 2.3125 13.9997C2.3125 20.2519 7.38089 25.3203 13.6331 25.3203Z"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {/* Input Field */}
                  <input
                    type="number"
                    name="screens.basic"
                    value={formData.screens?.basic ?? 0}
                    onChange={handleChange}
                    min="0"
                    className={`min-w-0 flex-1 px-3 py-2.5 text-sm text-center bg-transparent rounded-md border-none outline-none ${
                      theme === "dark"
                        ? "text-white placeholder-gray-600"
                        : "text-gray-900 placeholder-gray-400"
                    } focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                    style={{ minWidth: 0 }}
                  />

                  {/* Increment Button */}
                  <button
                    type="button"
                    onClick={() => incrementScreens("basic")}
                    className={`flex-shrink-0 px-2 h-10 w-10 flex items-center justify-center ${
                      theme === "dark" ? "hover:opacity-80" : "hover:opacity-70"
                    } transition-opacity`}
                    style={{ minWidth: 40 }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 28 28"
                      fill="none"
                    >
                      <path
                        d="M25.6919 13.9997C25.6919 7.74753 20.6235 2.67914 14.3714 2.67914C8.11917 2.67914 3.05078 7.74753 3.05078 13.9997C3.05078 20.2518 8.11917 25.3203 14.3714 25.3203C20.6235 25.3203 25.6919 20.2518 25.6919 13.9997Z"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                      />
                      <path
                        d="M14.3681 9.4715V18.528M18.8963 13.9997H9.83984"
                        stroke="#949494"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isCreating}
                className="px-8 py-1.5 rounded-md bg-[#B6CB24] text-black font-medium hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background:
                    "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                }}
              >
                {isCreating ? "Creating..." : "Create"}
              </button>
              <Link
                to={`${adminRoutes.gymList}`}
                className={`px-5 py-2 rounded-md font-medium ${
                  theme === "dark"
                    ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
              >
                Not Now
              </Link>
            </div>
          </form>
        </div>

        {/* Unassigned Screens Section */}
        <div
          className={`w-60 ${
            theme === "dark"
              ? "bg-[#1E1E1E] border border-gray-800"
              : "bg-white border border-gray-200"
          } rounded-lg p-5 h-fit`}
        >
          <div className="flex items-center justify-between mb-5">
            <h3
              className={`text-sm font-normal ${
                theme === "dark" ? "text-white" : "text-gray-800"
              }`}
            >
              Unassigned Screens
            </h3>
            <button
              className={`p-1 rounded ${
                theme === "dark" ? "hover:bg-gray-800" : "hover:bg-gray-100"
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M2.5 12C2.5 7.52166 2.5 5.28249 3.89124 3.89124C5.28249 2.5 7.52166 2.5 12 2.5C16.4783 2.5 18.7175 2.5 20.1088 3.89124C21.5 5.28249 21.5 7.52166 21.5 12C21.5 16.4783 21.5 18.7175 20.1088 20.1088C18.7175 21.5 16.4783 21.5 12 21.5C7.52166 21.5 5.28249 21.5 3.89124 20.1088C2.5 18.7175 2.5 16.4783 2.5 12Z"
                  stroke="#F3F3F6"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 16V11.5"
                  stroke="#C6D92C"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 8.01172V8.00172"
                  stroke="#C6D92C"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Monitor Container */}
          <div className="relative flex justify-center mb-4">
            {theme === "dark" ? (
              // existing dark monitor SVG (unchanged)
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="237"
                height="138"
                viewBox="0 0 237 138"
                fill="none"
              >
                <g clip-path="url(#clip0_278_7424)">
                  <path
                    opacity="0.45"
                    d="M204.119 134.69H31.7825C14.2301 134.69 0 120.457 0 102.898V31.792C0 14.2343 14.2301 0 31.7825 0H235.902V102.898C235.902 120.457 221.672 134.69 204.119 134.69Z"
                    fill="black"
                  />
                  <path
                    d="M234.531 132.222V2.46872L3.56676 2.46872V132.222H234.531Z"
                    fill="#0A0A0A"
                  />
                  <path
                    d="M234.53 2.46873L236.999 0H1.09766L3.56565 2.46873H234.53Z"
                    fill="url(#paint0_linear_278_7424)"
                  />
                  <path
                    d="M234.531 2.46873V132.222L237 134.69V0L234.531 2.46873Z"
                    fill="url(#paint1_linear_278_7424)"
                  />
                  <path
                    d="M234.53 132.222H3.56565L1.09766 134.69V134.692H236.999V134.69L234.53 132.222Z"
                    fill="url(#paint2_linear_278_7424)"
                  />
                  <path
                    d="M3.56565 2.46873L1.09766 0V134.69L3.56565 132.222V2.46873Z"
                    fill="url(#paint3_linear_278_7424)"
                  />
                  <path
                    d="M126.087 138H112.012C110.184 138 108.703 136.519 108.703 134.69H129.394C129.394 136.519 127.913 138 126.085 138H126.087Z"
                    fill="url(#paint4_linear_278_7424)"
                  />
                </g>
                <defs>
                  <linearGradient
                    id="paint0_linear_278_7424"
                    x1="119.049"
                    y1="2.49501"
                    x2="119.049"
                    y2="0.0425429"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop />
                    <stop offset="1" stopColor="#5C5C5C" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear_278_7424"
                    x1="234.531"
                    y1="67.3452"
                    x2="237"
                    y2="67.3452"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop />
                    <stop offset="1" stopColor="#5C5C5C" />
                  </linearGradient>
                  <linearGradient
                    id="paint2_linear_278_7424"
                    x1="119.049"
                    y1="134.692"
                    x2="119.049"
                    y2="132.379"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#5C5C5C" />
                    <stop offset="1" />
                  </linearGradient>
                  <linearGradient
                    id="paint3_linear_278_7424"
                    x1="1.09766"
                    y1="67.3452"
                    x2="3.56565"
                    y2="67.3452"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#5C5C5C" />
                    <stop offset="1" />
                  </linearGradient>
                  <linearGradient
                    id="paint4_linear_278_7424"
                    x1="119.049"
                    y1="138"
                    x2="119.049"
                    y2="134.692"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#5C5C5C" />
                    <stop offset="1" />
                  </linearGradient>
                  <clipPath id="clip0_278_7424">
                    <rect width="237" height="138" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            ) : (
              // light theme monitor: white screen with dark border
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="237"
                height="138"
                viewBox="0 0 237 138"
                fill="none"
              >
                <rect
                  x="1"
                  y="1"
                  width="235"
                  height="132"
                  rx="4"
                  fill="#FFFFFF"
                  stroke="#2C2C2C"
                  strokeWidth="2"
                />
                {/* small stand */}
                <rect
                  x="110"
                  y="134"
                  width="17"
                  height="3"
                  rx="1.5"
                  fill="#5C5C5C"
                />
              </svg>
            )}

            {/* Circular Badges Inside Monitor */}
            <div className="absolute flex gap-4" style={{ top: "38px" }}>
              {/* Premium Circle */}
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full border-2 border-[#B6CB24] bg-[#B6CB24]/10 flex items-center justify-center">
                  <span
                    className={`text-sm font-semibold ${
                      theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                  >
                    10
                  </span>
                </div>
                <span
                  className={`text-[10px] mt-1 ${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  {ScreenType.PREMIUM}
                </span>
              </div>

              {/* Normal Circle */}
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                    theme === "dark"
                      ? "border-gray-600 bg-gray-800"
                      : "border-gray-400 bg-gray-200"
                  }`}
                >
                  <span
                    className={`text-sm font-semibold ${
                      theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                  >
                    15
                  </span>
                </div>
                <span
                  className={`text-[10px] mt-1 ${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  {ScreenType.BASIC}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GymCreate;
